'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { SearchSelectBlock } from '@govinity/ui/components/form-blocks/search-select-block/index';
import type { SelectOption } from '@govinity/ui/components/form-blocks/select-block';
import { TextareaBlock } from '@govinity/ui/components/form-blocks/textarea-block';
import { Dropzone } from '@govinity/ui/components/modules/dropzone';
import { Button } from '@govinity/ui/components/ui/button';
import { ButtonLoadingSpinner } from '@govinity/ui/components/ui/button-loading-spinner';
import { Form } from '@govinity/ui/components/ui/form';
import { useToast } from '@govinity/ui/hooks/use-toast';
import { resolveMeetingConfig } from '@govinity/validation';
import type { AttachmentCreateDto } from '@govinity/validation';
import { PUBLIC_COMMENT_MAX_LENGTH } from '@govinity/validation/public';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { FileText, MessageSquarePlus, Upload, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import api from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { PUBLIC_COMMENT_MAX_ATTACHMENT_SIZE, type PublicCommentEntry } from './helpers';
import { useGovernmentContext } from '../../../context';
import { useMeetingContext } from '../meeting-context';

type CommentFormValues = {
  entry: string;
  comment: string;
};

type CommentFormProps = {
  entries: PublicCommentEntry[];
  onSuccess: () => void;
};

const toSelectOptions = (entries: PublicCommentEntry[]): SelectOption[] =>
  entries.map(({ depth, disabled, key, name }) => ({
    value: key,
    // Non-breaking spaces keep the nesting visible (plain spaces collapse).
    label: `${'  '.repeat(depth)}${name}`,
    disabled,
  }));

export const CommentForm = ({ entries, onSuccess }: CommentFormProps) => {
  const { government } = useGovernmentContext();
  const { meeting } = useMeetingContext();
  const user = useAuthStore((state) => state.user);
  const t = useTranslations('PortalPublicComments');
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);

  const { publicComments } = resolveMeetingConfig(government.meetingConfig);

  const commenterName = useMemo(
    () => [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '',
    [user]
  );

  const formSchema = useMemo(
    () =>
      z.object({
        entry: z.string().min(1, t('entryRequired')),
        comment: z
          .string()
          .trim()
          .min(1, t('commentRequired'))
          .max(PUBLIC_COMMENT_MAX_LENGTH, t('commentTooLong', { max: PUBLIC_COMMENT_MAX_LENGTH })),
      }),
    [t]
  );

  const form = useForm<CommentFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { entry: '', comment: '' },
  });

  const commentsApi = api.governments.publicComments(government.code, meeting.id);

  const uploadAttachment = async (attachmentFile: File): Promise<AttachmentCreateDto> => {
    const { filename, signedUrl } = await commentsApi.getPutSignedUrl(attachmentFile.name);

    await axios.put(signedUrl, attachmentFile, {
      headers: { 'Content-Type': attachmentFile.type },
    });

    return { name: attachmentFile.name, filename };
  };

  const { mutate: submitComment, isPending } = useMutation({
    mutationFn: async ({ comment, entry }: CommentFormValues) => {
      const target = entries.find(({ key }) => key === entry);

      if (!target) {
        throw new Error(t('entryRequired'));
      }

      const attachment = file ? await uploadAttachment(file) : undefined;

      return commentsApi.createUserComment({
        agendaSectionId: target.agendaSectionId,
        legislativeItemId: target.legislativeItemId,
        comment,
        attachment,
      });
    },
    onSuccess: () => {
      form.reset({ entry: '', comment: '' });
      setFile(null);
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: t('submitErrorTitle'),
        description: error instanceof Error ? error.message : t('submitErrorDescription'),
        variant: 'destructive',
      });
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => submitComment(values))} className="space-y-4">
        <SearchSelectBlock
          form={form}
          name="entry"
          label={t('entryLabel')}
          options={toSelectOptions(entries)}
          inputProps={{
            isDisabled: isPending,
            isOptionDisabled: (option) => !!option.disabled,
          }}
        />

        <TextareaBlock
          form={form}
          name="comment"
          label={t('commentLabel')}
          emptyAsNull={false}
          inputProps={{
            disabled: isPending,
            maxLength: PUBLIC_COMMENT_MAX_LENGTH,
            minHeight: 120,
            placeholder: t('commentPlaceholder'),
          }}
        />

        {publicComments.allowAttachments && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('attachmentLabel')}</p>
            {file ? (
              <div className="flex items-center justify-between gap-3 rounded border px-3 py-2">
                <span className="flex min-w-0 items-center gap-2 text-sm">
                  <FileText className="size-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{file.name}</span>
                </span>
                <Button
                  aria-label={t('removeAttachmentAriaLabel', { name: file.name })}
                  disabled={isPending}
                  onClick={() => setFile(null)}
                  size="xs"
                  variant="outline"
                >
                  <X aria-hidden="true" />
                  {t('removeAttachmentButton')}
                </Button>
              </div>
            ) : (
              <Dropzone
                accept={{ 'application/pdf': [] }}
                customization={{
                  icon: <Upload className="text-accent-foreground size-5" aria-hidden="true" />,
                  title: t('attachmentDropzoneTitle'),
                  description: t('attachmentDropzoneDescription'),
                }}
                disabled={isPending}
                maxFiles={1}
                maxSize={PUBLIC_COMMENT_MAX_ATTACHMENT_SIZE}
                onFilesChange={(files) => setFile(files[0] ?? null)}
              />
            )}
          </div>
        )}

        <p className="text-muted-foreground text-sm">
          {t('commenterNameNote', { name: commenterName })}
        </p>

        <ButtonLoadingSpinner
          aria-label={t('submitButton')}
          className="w-full"
          disabled={isPending}
          icon={<MessageSquarePlus aria-hidden="true" />}
          isLoading={isPending}
          text={t('submitButton')}
          type="submit"
        />
      </form>
    </Form>
  );
};
